import { useMemo, useState } from 'react';
import { i18n, translateDeviceStatus, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  SelectField,
  StatCard,
  type Column,
} from '@eon/ui';
import { hooks, type OmSessionListItem } from '@eon/api-client';
import { Activity, Cable, RefreshCw, Users, Zap } from 'lucide-react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ActiveChargingPanel } from '@/components/portal/ChargingControls';
import { formatDate, formatDateTime, formatDuration, formatKW, formatKWh } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { useSessionStatusFilterOptions } from '@/lib/session-status-filter';
import {
  ConnectorStatusBadge,
  SessionStatusBadge,
} from '@/components/status/StatusBadges';

const RANGE_OPTIONS = [
  { days: 30, labelKey: 'portal.dashboard.range30' },
  { days: 90, labelKey: 'portal.dashboard.range90' },
  { days: 180, labelKey: 'portal.dashboard.range180' },
] as const;

const SESSION_PAGE_SIZE_OPTIONS = [10, 25, 50];

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="bg-primary h-3.5 w-0.5 rounded-full" />
      <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-foreground/80">
        {children}
      </h3>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const sessionStatusOptions = useSessionStatusFilterOptions();
  const [rangeDays, setRangeDays] = useState(30);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPageSize, setSessionPageSize] = useState(10);
  const [sessionStatus, setSessionStatus] = useState('__all__');
  const [sessionFrom, setSessionFrom] = useState('');
  const [sessionTo, setSessionTo] = useState('');
  const stats = hooks.useOmDashboardStats();
  const energy = hooks.useOmDashboardEnergyChart(rangeDays);
  const stations = hooks.useOmDashboardStations();
  const ongoing = hooks.useOngoingCharge();
  const { sort: sessionSort, onSortChange: onSessionSortChange, sortParams: sessionSortParams } =
    useTableSort(() => setSessionPage(1));
  const sessions = hooks.useMySessions({
    page: sessionPage,
    pageSize: sessionPageSize,
    status: sessionStatus === '__all__' ? undefined : sessionStatus,
    from: sessionFrom || undefined,
    to: sessionTo || undefined,
    ...sessionSortParams,
  });
  const lang = i18n.language;

  const energyChartDateFormat = rangeDays > 90 ? 'MMM yy' : 'MMM d';
  const energyChartTickInterval =
    rangeDays > 90 ? Math.max(1, Math.floor(rangeDays / 12)) : rangeDays > 30 ? Math.max(1, Math.floor(rangeDays / 10)) : 0;

  const energyChartData = useMemo(() => {
    if (!energy.data?.length) return [];
    return energy.data.map((p) => ({
      time: formatDate(p.time, energyChartDateFormat, lang),
      kwh: p.consumption,
    }));
  }, [energy.data, lang, energyChartDateFormat]);

  const refreshAll = () => {
    void stats.refetch();
    void energy.refetch();
    void stations.refetch();
    void sessions.refetch();
    void ongoing.refetch();
  };

  const sessionTotalCount = sessions.data?.totalCount ?? 0;
  const sessionTotalPages = sessions.data?.totalPages ?? 1;

  const sessionColumns: Column<OmSessionListItem>[] = [
    { key: 'station', header: t('portal.sessions.station'), cell: (s) => s.stationName ?? '—' },
    { key: 'device', header: t('portal.sessions.device'), cell: (s) => s.deviceDisplayName ?? '—' },
    { key: 'user', header: t('portal.sessions.user'), cell: (s) => s.userName ?? '—' },
    {
      key: 'energy',
      header: t('session.energy'),
      sortable: true,
      sortValue: (s) => s.totalEnergyKWh,
      cell: (s) => formatKWh(s.totalEnergyKWh),
    },
    {
      key: 'duration',
      header: t('session.duration'),
      sortable: true,
      sortValue: (s) => s.durationSeconds,
      cell: (s) => formatDuration(s.durationSeconds),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      cell: (s) => <SessionStatusBadge status={String(s.status)} />,
    },
    {
      key: 'started',
      header: t('session.started'),
      sortable: true,
      sortKey: 'started',
      sortValue: (s) => s.startedAt,
      cell: (s) => (s.startedAt ? formatDateTime(s.startedAt, lang) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('nav.dashboard')}
        description={t('portal.dashboard.description')}
        actions={
          <Button variant="outline" onClick={refreshAll}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('actions.refresh')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('portal.dashboard.users')}
          value={stats.isLoading ? '—' : (stats.data?.userCount ?? 0)}
          icon={Users}
        />
        <StatCard
          label={t('portal.dashboard.sessions')}
          value={stats.isLoading ? '—' : (stats.data?.sessionCount ?? 0)}
          icon={Activity}
        />
        <StatCard
          label={t('portal.dashboard.devices')}
          value={stats.isLoading ? '—' : (stats.data?.deviceCount ?? 0)}
          icon={Cable}
        />
        <StatCard
          label={t('portal.dashboard.totalEnergy')}
          value={stats.isLoading ? '—' : formatKWh(stats.data?.totalUsedEnergy)}
          icon={Zap}
        />
      </div>

      <ActiveChargingPanel className="mt-6" />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
            <SectionTitle>{t('portal.dashboard.energyChart')}</SectionTitle>
            <div className="flex flex-wrap gap-1">
              {RANGE_OPTIONS.map((opt) => (
                <Button
                  key={opt.days}
                  size="sm"
                  variant={rangeDays === opt.days ? 'default' : 'outline'}
                  onClick={() => setRangeDays(opt.days)}
                >
                  {t(opt.labelKey)}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {energy.isLoading || energy.isFetching ? (
                <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                  {t('common.loading')}
                </div>
              ) : energyChartData.length === 0 ? (
                <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                  {t('common.noData')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={energyChartData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                    <defs>
                      <linearGradient id="portalEnergyGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(111,52%,52%)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(111,52%,52%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="time"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      interval={energyChartTickInterval}
                    />
                    <YAxis fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="kwh"
                      stroke="hsl(111,52%,52%)"
                      strokeWidth={2}
                      fill="url(#portalEnergyGrad)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <SectionTitle>{t('portal.dashboard.stations')}</SectionTitle>
          </CardHeader>
          <CardContent className="max-h-72 space-y-3 overflow-y-auto">
            {stations.isLoading ? (
              <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
            ) : (stations.data?.length ?? 0) === 0 ? (
              <p className="text-muted-foreground text-sm">{t('common.noData')}</p>
            ) : (
              stations.data?.map((station) => (
                <div key={station.id} className="rounded-lg border p-3">
                  <p className="font-medium">{station.name}</p>
                  {station.address ? (
                    <p className="text-muted-foreground text-xs">{station.address}</p>
                  ) : null}
                  <div className="mt-2 space-y-2">
                    {station.devices.map((device) => (
                      <div key={device.id}>
                        <p className="text-xs font-medium">
                          {device.displayName ?? device.id.slice(0, 8)}
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {translateDeviceStatus(t, device.status)}
                          </Badge>
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(device.connectors ?? []).length === 0 ? (
                            <span className="text-muted-foreground text-[10px]">
                              {t('portal.dashboard.noConnectors')}
                            </span>
                          ) : (
                            device.connectors?.map((c) => (
                              <div key={c.id} className="flex items-center gap-1">
                                <ConnectorStatusBadge
                                  id={c.id}
                                  status={c.status}
                                  className="text-[10px]"
                                />
                                {c.powerKW != null && c.powerKW > 0 ? (
                                  <span className="text-[10px] font-medium text-foreground/80">
                                    {formatKW(c.powerKW)}
                                  </span>
                                ) : null}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader className="pb-2">
          <SectionTitle>{t('portal.dashboard.recentSessions')}</SectionTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end">
            <SelectField
              className="w-full lg:w-44"
              value={sessionStatus}
              onValueChange={(v) => {
                setSessionStatus(v);
                setSessionPage(1);
              }}
              options={sessionStatusOptions}
              placeholder={t('common.status')}
            />
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs">{t('portal.sessions.dateFrom')}</label>
              <Input
                type="date"
                value={sessionFrom}
                onChange={(e) => {
                  setSessionFrom(e.target.value);
                  setSessionPage(1);
                }}
              />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs">{t('portal.sessions.dateTo')}</label>
              <Input
                type="date"
                value={sessionTo}
                onChange={(e) => {
                  setSessionTo(e.target.value);
                  setSessionPage(1);
                }}
              />
            </div>
          </div>
          <DataTable
            data={sessions.data?.data}
            columns={sessionColumns}
            loading={sessions.isLoading}
            rowKey={(s) => s.id}
            emptyTitle={t('common.noData')}
            sort={sessionSort}
            onSortChange={onSessionSortChange}
          />
          {sessions.data && sessionTotalCount > 0 ? (
            <Pagination
              className="mt-4"
              page={sessionPage}
              totalPages={sessionTotalPages}
              totalCount={sessionTotalCount}
              pageSize={sessionPageSize}
              pageSizeOptions={SESSION_PAGE_SIZE_OPTIONS}
              loading={sessions.isFetching}
              onPageChange={setSessionPage}
              onPageSizeChange={(size) => {
                setSessionPageSize(size);
                setSessionPage(1);
              }}
              labels={labels}
            />
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
